import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AbTestExperimentType } from '@appTypes/abTestExperiment';
import { useExperimentSeed } from '@hooks/useExperimentSeed';
import { resolveDocumentUrl } from '@hooks/Documents/useDocumentUrl';
import useSelector from '@redux/typedHooks';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { populateVariantsWithBinMaps } from './calculateABTestExperimentBins';
import { LoaderPage } from '../LoaderPage';

interface Props {
  data: AbTestExperimentType;
}

const preserveCurrentQueryString = (targetUrl: string, currentSearch: string) =>
  currentSearch ? `${targetUrl}${currentSearch}` : targetUrl;

export const ABTestExperiment = ({ data }: Props) => {
  const persistedExperimentSeed = useExperimentSeed();
  const navigate = useNavigate();
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const { mappedCategories } = useSelector((state) => state.sitestructure);
  const [didSendtrackingEvent, setDidSendTrackingEvent] = useState(false);

  // Decide which test group the user falls into and redirect to the correct one.
  useEffect(() => {
    if (
      persistedExperimentSeed &&
      data &&
      navigate &&
      mappedCategories &&
      dataLayer &&
      didSendtrackingEvent === false &&
      setDidSendTrackingEvent
    ) {
      const bins = populateVariantsWithBinMaps(data.variants);
      Object.values(bins).forEach((bin) => {
        if (persistedExperimentSeed >= bin.lowerLimit && persistedExperimentSeed <= bin.upperLimit) {
          const redirectUrl = resolveDocumentUrl(bin.reference, mappedCategories);
          if (!redirectUrl) {
            return;
          }

          const redirectUrlWithQueryString = preserveCurrentQueryString(redirectUrl, location.search);
          dataLayer.push({
            event: NAFGTMEvent.abExperimentParticipant,
            experimentId: data.experimentId,
            variantId: bin.variantId,
          });
          setDidSendTrackingEvent(true);
          navigate(redirectUrlWithQueryString, { replace: true });
        }
      });
    }
  }, [
    data,
    persistedExperimentSeed,
    navigate,
    location.search,
    dataLayer,
    mappedCategories,
    didSendtrackingEvent,
    setDidSendTrackingEvent,
  ]);

  return <LoaderPage />;
};
